import React, { useState, useEffect } from 'react';
import { Calendar, Trophy, Play, Video, CheckCircle, Flame } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { useProgress } from '../contexts/ProgressContext.js';
import CommentSection from '../components/comments/CommentSection.js';
import SafetyNotice from './common/SafetyNotice.js';

const DailyChallenge = ({ monthlyPackage, onVideoPlay, initialDay }) => {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { completeWorkout, workoutHistory } = useProgress();
  const [selectedDay, setSelectedDay] = useState(initialDay || 1);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [videoUrls, setVideoUrls] = useState({}); // Store generated video URLs
  const [completedDays, setCompletedDays] = useState([]);

  // Update selected day when initialDay changes (from URL hash)
  useEffect(() => {
    if (initialDay) {
      setSelectedDay(initialDay);
    }
  }, [initialDay]);

  const userTier = subscription?.tier || 'basic';
  const hasAccess = monthlyPackage?.availableTiers?.includes(userTier);

  // Use dynamic daily challenge data from monthlyPackage
  const dailyChallengeData = monthlyPackage?.dailyChallenge || [];

  const currentDayExercise = dailyChallengeData.find(ex => ex.day === selectedDay);

  // Load user's completed days from progress
  useEffect(() => {
    console.log('Loading completed days - user:', user, 'workoutHistory:', workoutHistory);
    console.log('MonthlyPackage subscriptionMonth:', monthlyPackage?.subscriptionMonth);
    
    if (user && workoutHistory && Array.isArray(workoutHistory) && monthlyPackage?.subscriptionMonth) {
      try {
        const month = monthlyPackage.subscriptionMonth;
        // Look for Challenge workouts in the history that match this specific month
        const challengeDays = workoutHistory
          .filter(workout => {
            if (!workout.id) return false;
            
            // For month 1, match both old format (challenge-day-X) and new format (month1-challenge-day-X)
            // For months 2+, only match new format (monthX-challenge-day-X)
            if (month === 1) {
              const hasChallenge = workout.id.includes(`month1-challenge-day-`) || 
                                  (workout.id.includes('challenge-day-') && !workout.id.includes('month'));
              console.log('Checking workout:', workout.id, 'for month 1, matches:', hasChallenge);
              return hasChallenge;
            } else {
              const hasChallenge = workout.id.includes(`month${month}-challenge-day-`);
              console.log('Checking workout:', workout.id, 'for month', month, 'matches:', hasChallenge);
              return hasChallenge;
            }
          })
          .map(workout => {
            // Extract day number from workout ID
            // New format: "month3-challenge-day-1" -> 1
            // Old format (month 1 only): "challenge-day-1" -> 1
            let dayMatch;
            if (workout.id.includes(`month${month}-challenge-day-`)) {
              dayMatch = workout.id.match(new RegExp(`month${month}-challenge-day-(\\d+)`));
            } else if (month === 1 && workout.id.includes('challenge-day-') && !workout.id.includes('month')) {
              // Legacy format - only for month 1
              dayMatch = workout.id.match(/challenge-day-(\d+)/);
            }
            const day = dayMatch ? parseInt(dayMatch[1]) : null;
            console.log('Extracted day from', workout.id, ':', day);
            return day;
          })
          .filter(day => day !== null);
        
        console.log('Found Challenge days for month', month, ':', challengeDays);
        setCompletedDays(challengeDays);
      } catch (error) {
        console.error('Error loading completed days:', error);
        setCompletedDays([]);
      }
    } else {
      console.log('No user, workoutHistory, or subscriptionMonth, starting with empty array');
      // Start with empty array if no progress data
      setCompletedDays([]);
    }
  }, [user, workoutHistory, monthlyPackage?.subscriptionMonth]);

  const handlePlayVideo = async (exercise) => {
    if (!hasAccess) {
      alert('Please upgrade to Premium or Elite to view this video.');
      return;
    }

    const videoId = exercise.cloudflareStreamId;
    
    if (!videoId) {
      console.warn('No video ID found for:', exercise.title);
      alert('Video not available for this exercise yet.');
      return;
    }

    setLoadingVideo(true);
    try {
      // Check if we already have the video URL
      if (videoUrls[videoId]) {
        onVideoPlay(videoUrls[videoId], exercise);
        return;
      }

      // Generate signed URL using backend API (same as WorkoutDetail)
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';
      const response = await fetch(`${apiBaseUrl}/cloudflare-stream/get-video-url?videoId=${videoId}&tier=${userTier}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to generate secure URL: ${response.statusText}`);
      }
      const data = await response.json();
      const videoUrl = data.url;

      // Store the URL for future use
      setVideoUrls(prev => ({
        ...prev,
        [videoId]: videoUrl
      }));

      onVideoPlay(videoUrl, exercise);
    } catch (error) {
      console.error('Error loading video:', error);
      alert('Failed to load video. Please try again.');
    } finally {
      setLoadingVideo(false);
    }
  };

  const handleCompleteDay = async (day) => {
    if (!completedDays.includes(day)) {
      try {
        const dayExercise = dailyChallengeData.find(ex => ex.day === day);
        if (dayExercise && user && completeWorkout) {
          // Estimate duration (5 minutes per day for challenge)
          const durationMinutes = 5;
          
          // Get month number from monthlyPackage
          const month = monthlyPackage?.subscriptionMonth || 1;
          
          // Create workout completion data with month identifier
          const workoutId = `month${month}-challenge-day-${day}`;
          const exercisesCompleted = [{
            exerciseId: `month${month}-challenge-day-${day}`,
            name: dayExercise.title || `Challenge Day ${day}`,
            reps: 1,
            duration: durationMinutes
          }];
          
          // Save to progress system using completeWorkout
          const success = await completeWorkout(workoutId, exercisesCompleted, durationMinutes);
          
          if (success) {
            setCompletedDays(prev => [...prev, day]);
            console.log(`Challenge Day ${day} completed and saved to progress!`);
          } else {
            console.error('Failed to save challenge progress');
          }
        } else {
          // Fallback if completeWorkout is not available
          setCompletedDays(prev => [...prev, day]);
          console.log(`Challenge Day ${day} completed!`);
        }
      } catch (error) {
        console.error('Error completing challenge day:', error);
        // Still mark as completed locally even if progress save fails
        setCompletedDays(prev => [...prev, day]);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <Calendar className="w-6 h-6 mr-2 text-blue-600" /> {monthlyPackage?.title || 'Daily Challenge'}
      </h2>

      <SafetyNotice className="mb-6" />
      
      {/* Day Navigation */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {dailyChallengeData.map((exercise) => (
          <button
            key={exercise.day}
            onClick={() => setSelectedDay(exercise.day)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              selectedDay === exercise.day
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Day {exercise.day}
          </button>
        ))}
      </div>

      {/* Current Day Exercise */}
      {currentDayExercise && (
        <div id={`challenge-day-${currentDayExercise.day}`} className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {currentDayExercise.title}
            </h3>
            <p className="text-lg text-blue-600 font-medium mb-4">
              {currentDayExercise.movement}
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              {currentDayExercise.description}
            </p>
          </div>

          {/* Why it matters */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
            <h4 className="font-bold text-blue-800 mb-3 text-lg">Why it matters:</h4>
            <p className="text-blue-700 leading-relaxed">
              {currentDayExercise.whyItMatters}
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
            <h4 className="font-bold text-green-800 mb-3 text-lg">Today's Movement:</h4>
            <p className="text-green-700 leading-relaxed">
              {currentDayExercise.instructions}
            </p>
          </div>

          {/* Workout */}
          <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-lg">
            <h4 className="font-bold text-orange-800 mb-3 text-lg">Today's Stacked Workout:</h4>
            <div className="text-orange-700 leading-relaxed whitespace-pre-line">
              {currentDayExercise.workout}
            </div>
          </div>

          {/* Form Tip */}
          <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg">
            <h4 className="font-bold text-purple-800 mb-3 text-lg">Form Tip:</h4>
            <p className="text-purple-700 leading-relaxed">
              {currentDayExercise.formTip}
            </p>
          </div>

          {/* Daily Win */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <h4 className="font-bold text-yellow-800 mb-3 text-lg flex items-center">
              <Flame className="w-5 h-5 mr-2" /> Your Daily Win:
            </h4>
            <p className="text-yellow-700 leading-relaxed">
              {currentDayExercise.dailyWin}
            </p>
          </div>

          {/* Video Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
            <div>
              <h4 className="font-bold text-gray-800 mb-4 text-lg">Watch the Movement:</h4>
              <button
                onClick={() => handlePlayVideo(currentDayExercise)}
                disabled={loadingVideo || !hasAccess}
                className={`w-full flex items-center justify-center px-6 py-4 rounded-lg font-medium transition-colors text-lg ${
                  hasAccess
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {loadingVideo ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-3" />
                    {hasAccess ? 'Watch Video' : 'Upgrade to Watch'}
                  </>
                )}
              </button>
            </div>

            {/* Video Preview/Thumbnail */}
            <div className="bg-gray-100 rounded-lg flex items-center justify-center min-h-[300px]">
              {hasAccess ? (
                <div className="text-center">
                  <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Click "Watch Video" to start</p>
                </div>
              ) : (
                <div className="text-center">
                  <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Upgrade to Premium or Elite to access videos</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completion Button */}
      {currentDayExercise && (
        <div className="mt-6 text-center">
          <button
            onClick={() => handleCompleteDay(selectedDay)}
            disabled={completedDays.includes(selectedDay)}
            className={`px-8 py-4 rounded-lg font-medium transition-colors text-lg ${
              completedDays.includes(selectedDay)
                ? 'bg-green-100 text-green-800 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {completedDays.includes(selectedDay) ? (
              <>
                <CheckCircle className="w-6 h-6 inline mr-2" />
                Day {selectedDay} Completed
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6 inline mr-2" />
                Mark Day {selectedDay} as Complete
              </>
            )}
          </button>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Challenge Progress</span>
          <span className="text-sm text-gray-500">
            {selectedDay} of {dailyChallengeData.length} days
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${(selectedDay / dailyChallengeData.length) * 100}%` }}
          ></div>
        </div>
        <div className="mt-2 text-center">
          <span className="text-sm text-gray-600">
            {selectedDay === dailyChallengeData.length ? "🎉 Challenge Complete!" : `${dailyChallengeData.length - selectedDay} days remaining`}
          </span>
        </div>
      </div>

      {/* Comments Section */}
      {currentDayExercise && monthlyPackage?._id && (
        <div className="mt-12">
          <CommentSection 
            targetType="dailyChallenge" 
            targetId={`${monthlyPackage._id}-day-${selectedDay}`}
          />
        </div>
      )}
    </div>
  );
};

export default DailyChallenge;



